// components/molecules/CreateGroupForm.tsx
import { useState, useRef } from "react";
import { useForm, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { LocationSelector } from "./LocationSelector";
import Group3 from "@/assets/images/icons/group-3.svg";
import { Add, CloseCircle, Gps } from "iconsax-react";

export interface CreateGroupFormData {
  title: string;
  description: string;
  activityType: "friendship" | "dating" | "networking" | "events" | "other";
  privacySetting: "public" | "private" | "hidden";
  location: string;
  latitude?: number;
  longitude?: number;
  zipCode?: string;
  expirationDate?: string;
  isPaid: boolean;
  membershipFee?: number;
  paymentType?: "one-time" | "recurring";
  paymentMethod?: string;
  image?: File;
  video?: File;
}

const createGroupSchema = z.object({
  title: z.string().min(1, "Group name is required"),
  description: z.string().min(1, "Description is required"),
  activityType: z.enum([
    "friendship",
    "dating",
    "networking",
    "events",
    "other",
  ]),
  privacySetting: z.enum(["public", "private", "hidden"]),
  location: z.string().min(1, "Location is required"),
  latitude: z.number().optional(),
  longitude: z.number().optional(),
  zipCode: z.string().optional(),
  expirationHours: z.string(),
  isPaid: z.boolean(),
  membershipFee: z.preprocess(
    (val) =>
      val === "" || Number.isNaN(Number(val)) ? undefined : Number(val),
    z
      .number()
      .optional()
      .refine((val) => val === undefined || val > 0, {
        message: "Amount must be greater than 0",
      })
  ),
  paymentType: z.enum(["one-time", "recurring"]).optional(),
  paymentMethod: z.string().optional(),
});

type CreateGroupFormProps = {
  onSubmit: (formData: CreateGroupFormData) => void;
  onCancel: () => void;
  isLoading?: boolean;
};

export const CreateGroupForm = ({
  onSubmit,
  onCancel,
  isLoading = false,
}: CreateGroupFormProps) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm({
    resolver: zodResolver(createGroupSchema),
    defaultValues: {
      title: "",
      description: "",
      activityType: "friendship" as const,
      privacySetting: "public" as const,
      location: "",
      latitude: undefined,
      longitude: undefined,
      zipCode: "",
      expirationHours: "",
      isPaid: false,
      membershipFee: undefined,
      paymentType: "one-time" as const,
      paymentMethod: "credit-card",
    },
  });

  const isPaid = watch("isPaid");

  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [showLocationSelector, setShowLocationSelector] = useState(false);

  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const onSubmitHandler: SubmitHandler<any> = (data) => {
    let expirationDate: string | undefined;

    if (data.expirationHours) {
      const hours = parseInt(data.expirationHours);
      const date = new Date();
      date.setHours(date.getHours() + hours);
      expirationDate = date.toISOString();
    }

    // Build form data matching API structure
    const formData: CreateGroupFormData = {
      title: data.title,
      description: data.description,
      activityType: data.activityType,
      privacySetting: data.privacySetting,
      location: data.location,
      latitude: data.latitude,
      longitude: data.longitude,
      zipCode: data.zipCode,
      expirationDate: expirationDate, // Can be undefined if "never" (empty string)
      isPaid: data.isPaid,
      membershipFee: data.isPaid ? data.membershipFee : undefined,
      paymentType: data.isPaid ? data.paymentType : undefined,
      paymentMethod: data.isPaid ? data.paymentMethod : undefined,
      image: imageFile || undefined,
      video: videoFile || undefined,
    };

    onSubmit(formData);
  };

  const handleImageUpload = () => {
    imageInputRef.current?.click();
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        alert("Image size must be less than 5MB");
        return;
      }
      setImageFile(file);
      const url = URL.createObjectURL(file);
      setImageUrl(url);
    }
  };

  const handleVideoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("video/")) {
        alert("Please select a video file");
        return;
      }
      if (file.size > 50 * 1024 * 1024) {
        alert("Video size must be less than 50MB");
        return;
      }
      setVideoFile(file);
    }
  };

  const toggleLocationSelector = () => {
    setShowLocationSelector(!showLocationSelector);
  };

  const handleLocationSelected = (location: {
    address: string;
    latitude: number;
    longitude: number;
  }) => {
    setValue("location", location.address);
    setValue("latitude", location.latitude);
    setValue("longitude", location.longitude);
    setShowLocationSelector(false);
  };

  return (
    <div className="flex flex-col h-full text-dark-text">
      <div className="flex-1 overflow-y-auto p-4">
        <form
          id="create-group-form"
          onSubmit={handleSubmit(onSubmitHandler)}
          className="flex flex-col gap-4"
        >
          {/* Group Image */}
          <div>
            <label className="block text-sm font-medium mb-4 text-dark-text">
              Group Image
            </label>
            <div className="flex items-center gap-[13px]">
              <div className="relative">
                <div
                  className="w-[58px] h-[58px] rounded-full bg-dark-20 flex items-center justify-center overflow-hidden cursor-pointer"
                  onClick={handleImageUpload}
                >
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt="Group"
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <img src={Group3} alt="Group" />
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={handleImageUpload}
                className="flex items-center gap-1 py-1.5 px-5 border border-dark-30 rounded-[5px] text-dark-text bg-dark-20 cursor-pointer"
                disabled={isLoading}
              >
                <Add size={18} color="#FEFEFE" />
                <p className="text-sm">Upload Image</p>
              </button>

              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageChange}
                className="hidden"
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Group Title */}
          <div>
            <label className="block text-sm font-medium mb-2.5 text-dark-text">
              Group Name
            </label>
            <input
              {...register("title")}
              placeholder="Group Name"
              className="w-full px-4 py-2 border border-dark-30 rounded-[5px] focus:ring focus:ring-blue-200 text-dark-text"
              disabled={isLoading}
            />
            {errors.title && (
              <p className="text-red-500 text-xs mt-1">
                {errors.title.message}
              </p>
            )}
          </div>

          {/* Group Description */}
          <div>
            <label className="block text-sm font-medium mb-2.5 text-dark-text">
              Group Description
            </label>
            <textarea
              {...register("description")}
              placeholder="Description"
              className="w-full px-4 py-2 border border-dark-30 rounded-[5px] focus:ring focus:ring-blue-200 text-dark-text"
              disabled={isLoading}
            />
            {errors.description && (
              <p className="text-red-500 text-xs mt-1">
                {errors.description.message}
              </p>
            )}
          </div>

          {/* Video Upload */}
          <div>
            <label className="block text-sm font-medium mb-2.5 text-dark-text">
              Group Video (Optional)
            </label>
            <div className="flex items-center gap-2">
              <input
                ref={videoInputRef}
                type="file"
                accept="video/*"
                onChange={handleVideoChange}
                className="hidden"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => videoInputRef.current?.click()}
                className="flex items-center gap-1 py-1.5 px-5 border border-dark-30 rounded-[5px] text-dark-text bg-dark-20"
                disabled={isLoading}
              >
                <Add size={18} color="#FEFEFE" />
                <p className="text-sm">
                  {videoFile ? "Change" : "Upload"} Video
                </p>
              </button>
              {videoFile && (
                <span className="text-sm text-dark-text">{videoFile.name}</span>
              )}
            </div>
          </div>

          {/* Activity Type */}
          <div>
            <label className="block text-sm font-medium mb-2.5 text-dark-text">
              Activity Type
            </label>
            <select
              {...register("activityType")}
              className="w-full px-4 py-2 border border-dark-30 rounded-[5px] focus:ring focus:ring-blue-200 text-dark-text"
              disabled={isLoading}
            >
              <option value="friendship">Friendship</option>
              <option value="dating">Dating</option>
              <option value="networking">Networking</option>
              <option value="events">Events</option>
              <option value="other">Other</option>
            </select>
            {errors.activityType && (
              <p className="text-red-500 text-xs mt-1">
                {errors.activityType.message}
              </p>
            )}
          </div>

          {/* Privacy Setting */}
          <div>
            <label className="block text-sm font-medium mb-2.5 text-dark-text">
              Privacy Setting
            </label>
            <select
              {...register("privacySetting")}
              className="w-full px-4 py-2 border border-dark-30 rounded-[5px] focus:ring focus:ring-blue-200 text-dark-text"
              disabled={isLoading}
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
              <option value="hidden">Hidden</option>
            </select>
            {errors.privacySetting && (
              <p className="text-red-500 text-xs mt-1">
                {errors.privacySetting.message}
              </p>
            )}
          </div>

          {/* Group Location */}
          <div>
            <label className="block text-sm font-medium mb-2.5 text-dark-text">
              Group Location
            </label>
            <div className="flex gap-2">
              <input
                {...register("location")}
                placeholder="Group Location"
                className="flex-1 px-4 py-2 border border-dark-30 rounded-[5px] focus:ring focus:ring-blue-200 text-dark-text"
                disabled={isLoading}
                readOnly
              />
              <button
                type="button"
                onClick={toggleLocationSelector}
                disabled={isLoading}
                className="px-4 py-2 border border-dark-30 rounded-[5px] bg-dark-20 hover:bg-dark-30 transition-colors disabled:opacity-50 text-dark-text text-sm whitespace-nowrap"
                title="Select location on map"
              >
                {showLocationSelector ? (
                  <CloseCircle size={20} color="#FEFEFE" />
                ) : (
                  <Gps size={20} color="#FEFEFE" />
                )}
              </button>
            </div>
            {errors.location && (
              <p className="text-red-500 text-xs mt-1">
                {errors.location.message}
              </p>
            )}

            {/* Location Selector */}
            {showLocationSelector && (
              <div className="mt-4">
                <LocationSelector
                  onLocationSelected={handleLocationSelected}
                  initialLocation={
                    watch("location") && watch("latitude") && watch("longitude")
                      ? {
                          address: watch("location"),
                          latitude: watch("latitude")!,
                          longitude: watch("longitude")!,
                        }
                      : undefined
                  }
                />
              </div>
            )}
          </div>

          {/* Zip Code */}
          <div>
            <label className="block text-sm font-medium mb-2.5 text-dark-text">
              Zip Code
            </label>
            <input
              {...register("zipCode")}
              placeholder="Zip Code"
              className="w-full px-4 py-2 border border-dark-30 rounded-[5px] focus:ring focus:ring-blue-200 text-dark-text"
              disabled={isLoading}
            />
            {errors.zipCode && (
              <p className="text-red-500 text-xs mt-1">
                {errors.zipCode.message}
              </p>
            )}
          </div>

          {/* Expiration Time */}
          <div>
            <label className="block text-sm font-medium mb-2.5 text-dark-text">
              Expiration Time
            </label>
            <select
              {...register("expirationHours")}
              className="w-full px-4 py-2 border border-dark-30 rounded-[5px] focus:ring focus:ring-blue-200 text-dark-text"
              disabled={isLoading}
            >
              <option value="12">12 hours</option>
              <option value="24">24 hours</option>
              <option value="48">48 hours</option>
              <option value="72">72 hours</option>
              <option value="">Never</option>
            </select>
            {errors.expirationHours && (
              <p className="text-red-500 text-xs mt-1">
                {errors.expirationHours.message}
              </p>
            )}
          </div>

          {/* Group Type (Paid/Free) */}
          <div>
            <label className="block text-sm font-medium mb-1 text-dark-text">
              Group Type
            </label>
            <div className="flex flex-col gap-3">
              <label className="flex items-center gap-2 text-dark-text cursor-pointer">
                <div className="relative flex items-center justify-center">
                  <input
                    type="radio"
                    checked={isPaid === false}
                    onChange={() => setValue("isPaid", false)}
                    className="peer appearance-none cursor-pointer w-5 h-5 border border-dark-90 rounded-full checked:border-dark-text"
                    disabled={isLoading}
                  />
                  {isPaid === false && (
                    <div className="absolute w-3 h-3 bg-dark-text rounded-full pointer-events-none" />
                  )}
                </div>
                Free
              </label>

              <label className="flex items-center gap-2 text-dark-text cursor-pointer">
                <div className="relative flex items-center justify-center">
                  <input
                    type="radio"
                    checked={isPaid === true}
                    onChange={() => setValue("isPaid", true)}
                    className="peer appearance-none cursor-pointer w-5 h-5 border border-dark-90 rounded-full checked:border-dark-text"
                    disabled={isLoading}
                  />
                  {isPaid === true && (
                    <div className="absolute w-3 h-3 bg-dark-text rounded-full pointer-events-none" />
                  )}
                </div>
                Paid
                <input
                  {...register("membershipFee")}
                  type="number"
                  placeholder="Input Amount"
                  disabled={!isPaid || isLoading}
                  className="w-40 px-2 py-1 border border-dark-30 rounded-[5px] focus:ring focus:ring-blue-200 ml-2 text-dark-text disabled:opacity-50"
                />
              </label>
            </div>

            {errors.isPaid && (
              <p className="text-red-500 text-xs mt-1">
                {errors.isPaid.message}
              </p>
            )}
            {errors.membershipFee && (
              <p className="text-red-500 text-xs mt-1">
                {errors.membershipFee.message}
              </p>
            )}
          </div>

          {/* Payment Details (Only if Paid) */}
          {isPaid && (
            <div className="flex flex-col gap-4 mt-2">
              <div>
                <label className="block text-sm font-medium mb-2.5 text-dark-text">
                  Payment Type
                </label>
                <select
                  {...register("paymentType")}
                  className="w-full px-4 py-2 border border-dark-30 rounded-[5px] focus:ring focus:ring-blue-200 text-dark-text"
                  disabled={isLoading}
                >
                  <option value="one-time">One-time</option>
                  <option value="recurring">Recurring</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2.5 text-dark-text">
                  Payment Method
                </label>
                <select
                  {...register("paymentMethod")}
                  className="w-full px-4 py-2 border border-dark-30 rounded-[5px] focus:ring focus:ring-blue-200 text-dark-text"
                  disabled={isLoading}
                >
                  <option value="credit-card">Credit Card</option>
                  <option value="paypal">PayPal</option>
                  <option value="stripe">Stripe</option>
                </select>
              </div>
            </div>
          )}
        </form>
      </div>

      {/* Footer */}
      <div className="pt-2.5">
        <div className="flex justify-end items-center gap-4">
          <button
            onClick={onCancel}
            disabled={isLoading}
            className="py-2 px-4 text-dark-text border border-dark-30 rounded-[5px] hover:bg-dark-20 transition text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="create-group-form"
            disabled={isLoading}
            className="py-2 px-4 bg-dark-text text-dark-5 rounded-[5px] hover:bg-dark-100 transition text-sm font-medium cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-dark-5"></div>
                Creating...
              </>
            ) : (
              "Create Group"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
